import { useCallback, useEffect, useMemo, useState } from 'react';
import getEntityKey from 'helpers/getEntityKey';
import { IEntity } from 'types/shared';
import { ISocialPostTrends } from '../SocialArtistPerformanceTrend/types';
import { CountryIds } from 'types/shared';

export default useFilteredTrends;

interface IFilteredTrend {
  breakdown?: string | number;
  countryId?: CountryIds;
  data: any[];
  entity: IEntity;
  hasNoData?: boolean;
  label?: string;
  totalForPeriod?: number;
}

interface IFilteredTrends {
  filteredTrends: IFilteredTrend[];
  disabledTrends: string[];
  toggleTrend: (event: any) => void;
}
/**
 * This hook allows you to filter the trends in a trend graph.
 * The returned handler `toggleTrend` can be bound to a DOM element
 * in order to toggle trend lines by passing an id.
 *
 * @param trends An array of trends.
 * @param obj
 * @param obj.filteredTrends The trends array filtered by the disabled trends
 * @param obj.disabledTrend An array of disabled trends' ids.
 * @return obj.toggleTrend Handler used to toggle a trend
 */
function useFilteredTrends(trends: ISocialPostTrends[]): IFilteredTrends {
  const [disabledTrends, setDisabledTrends] = useState<string[]>([]);

  /**
   * If trend data updates, check if all disabled trends are
   * still valid. Remove the invalid ones.
   */
  useEffect(() => {
    if (!trends) return;

    const keys = trends.map((t) => getEntityKey(t));

    // Are all disabled trends valid?
    const hasOnlyValidKeys = disabledTrends.every((key) => keys.includes(key));
    if (hasOnlyValidKeys) return;

    // Don't allow disabled for 2 or less trends.
    if (disabledTrends.filter((key) => keys.includes(key)).length <= 2) {
      return setDisabledTrends([]);
    }

    setDisabledTrends(disabledTrends.filter((key) => keys.includes(key)));
  }, [disabledTrends, trends]);

  /**
   * Memoize the filtered trends
   * @type {Array}
   */
  const filteredTrends = useMemo(() => {
    if (disabledTrends.length > 0) {
      return trends.filter(byDisabledTrends);
    }

    function byDisabledTrends(trend: ISocialPostTrends): boolean {
      return !disabledTrends.includes(getEntityKey(trend));
    }

    return trends;
  }, [disabledTrends, trends]);

  /**
   * Add/remove a trend id from the disabledTrends array.
   *
   * @param  {String} id The id of a trend, generated by getEntityKey
   */
  const toggleTrend = useCallback(
    (id) => {
      if (disabledTrends.includes(id)) {
        return setDisabledTrends(disabledTrends.filter((d) => d !== id));
      }

      setDisabledTrends([...disabledTrends, id]);
    },
    [disabledTrends]
  );

  return { filteredTrends, disabledTrends, toggleTrend };
}
